import { useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import { OPEN_WORK_ORDER_STATES, useAvailability, useWorkOrders, type WorkOrderRow } from '../data/safety.ts'
import type { AssetRow } from '../data/fleet.ts'
import { ASSET_SECTIONS, durationText } from '../data/workshopUi.ts'
import { formatLocal } from '../lib/time.ts'
import { Banner, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import type { WorkOrderDowntime } from '../sync/transport.ts'
import { WorkOrderParts } from './WorkOrderParts.tsx'
import { History } from '../ui/History.tsx'
import { TwoTap, useCloseOnPending } from './workshop/shared.tsx'

const STATE_TONE: Record<WorkOrderRow['state'], 'gray' | 'amber' | 'green' | 'red'> = { open: 'amber', in_progress: 'amber', awaiting_parts: 'amber', repaired: 'green', closed: 'gray', cancelled: 'gray' }

/** The workshop's side of one machine: its work orders (update, mark repaired with return-to-service notes, cancel), a new work order, and a
 *  manual restriction. All online-first: the screen waits for the server's answer; with no network the command is kept on the device and the
 *  form closes on an amber "kept on this device" line (M23 item 10), never a red error over a form left open. */
export function WorkOrderPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const manage = useWriteAccess('workorder.manage')
  const restrict = useWriteAccess('asset.restrict')
  const orders = useWorkOrders()
  const [showClosed, setShowClosed] = useState(false)
  const mine = (orders ?? []).filter((w) => w.asset_id === asset.id)
  const open = mine.filter((w) => OPEN_WORK_ORDER_STATES.includes(w.state) || w.state === 'repaired').sort((a, b) => (a.opened_at < b.opened_at ? 1 : -1))
  const done = mine.filter((w) => w.state === 'closed' || w.state === 'cancelled').sort((a, b) => ((a.closed_at ?? '') < (b.closed_at ?? '') ? 1 : -1))
  return (
    <section id={ASSET_SECTIONS.workorders} className="flex scroll-mt-20 flex-col gap-3" data-testid="workorder-panel">
      <h3 className="text-lg font-bold">{t('workorder.title')}</h3>
      {open.length === 0 ? <p className="opacity-70" data-testid="no-orders">{t('workorder.none')}</p> : (
        <ul className="flex flex-col gap-2" data-testid="order-list">
          {open.map((w) => <OrderCard key={w.id} order={w} assetId={asset.id} tz={site.timezone} canManage={manage.allowed} />)}
        </ul>
      )}
      {done.length > 0 && (
        <>
          <label className="flex min-h-14 items-center gap-3 font-medium"><input type="checkbox" className="size-6" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />{t('workorder.showClosed', { count: done.length })}</label>
          {showClosed && <ul className="flex flex-col gap-2">{done.map((w) => <OrderCard key={w.id} order={w} assetId={asset.id} tz={site.timezone} canManage={false} />)}</ul>}
        </>
      )}
      {manage.allowed && asset.lifecycle !== 'retired' && <OpenOrderForm assetId={asset.id} />}
      {restrict.allowed && asset.lifecycle !== 'retired' && <RestrictForm assetId={asset.id} />}
    </section>
  )
}

/** What the order cost so far (M23 item 44): the downtime of its defects' restrictions and the labour clocked on it, summed by the server
 *  when online (`useRpc(...).fresh`: a number for THIS order, never a stale one), "—" offline. */
function Downtime({ order }: { order: Pending<WorkOrderRow> }) {
  const { t } = useTranslation()
  const { org } = useClub()
  const q = useRpc<WorkOrderDowntime>('work_order_downtime', { p_org: org.org_id, p_work_order: order.id }, !order._pending)
  if (order._pending) return null
  if (q.online === false) return <p className="meta" data-testid="order-downtime" data-state="offline">{t('workorder.downtimeOffline')}</p>
  if (q.error) return null
  if (!q.data || !q.fresh) return <p className="meta" data-testid="order-downtime" data-state="loading">{t('workorder.downtime')} : —</p>
  const d = q.data
  const open = (yes: boolean) => (yes ? ` (${t('workorder.downtimeOpen')})` : '')
  return (
    <p className="meta" data-testid="order-downtime" data-state="ready">
      {t('workorder.downtime')} : <span className="font-semibold">{durationText(d.downtime_seconds, t)}</span>{open(d.downtime_open)}
      {' · '}{t('workorder.labour')} : <span className="font-semibold">{durationText(d.labour_seconds, t)}</span>{open(d.labour_open)}
      {/* a manual entry still awaiting the review is said apart, never shown as clocked (review of item 44) */}
      {d.labour_pending_seconds > 0 && <span data-testid="order-labour-pending"> (+ {durationText(d.labour_pending_seconds, t)} {t('workorder.labourPending')})</span>}
    </p>
  )
}

function OrderCard({ order, assetId, tz, canManage }: { order: Pending<WorkOrderRow>; assetId: string; tz: string; canManage: boolean }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const labour = useWriteAccess('workorder.labour')
  const [mode, setMode] = useState<'view' | 'update' | 'repaired'>('view')
  const [state, setState] = useState<'open' | 'in_progress' | 'awaiting_parts'>(OPEN_WORK_ORDER_STATES.includes(order.state) ? (order.state as 'open') : 'open')
  const [diagnosis, setDiagnosis] = useState(order.diagnosis ?? '')
  const [performed, setPerformed] = useState(order.service_performed ?? '')
  const [rts, setRts] = useState(order.rts_notes ?? '')
  const live = OPEN_WORK_ORDER_STATES.includes(order.state)
  useCloseOnPending(w.pending, () => setMode('view'))
  const save = (e: FormEvent) => {
    e.preventDefault()
    void w.run('workorder.update@1', {
      work_order_id: order.id, expected_revision: order.revision,
      set: { state, diagnosis: diagnosis.trim() || null, service_performed: performed.trim() || null },
    }, 'workorder.saved').then((ok) => { if (ok) setMode('view') })
  }
  const repaired = (e: FormEvent) => {
    e.preventDefault()
    void w.run('workorder.mark_repaired@1', { work_order_id: order.id, rts_notes: rts.trim() }, 'workorder.repairedDone').then((ok) => { if (ok) setMode('view') })
  }
  return (
    <li className="card flex flex-col gap-2" data-testid="order-row" data-order={order.id}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={STATE_TONE[order.state]}>{t(`workorder.state.${order.state}`)}</Pill>
        <span className="text-sm font-semibold">{t(`workorder.kind.${order.kind}`)}</span>
        <span className="meta">{formatLocal(order.opened_at, tz, 'dd/MM/yyyy')}</span>
      </div>
      {order.complaint && <p className="font-semibold">{order.complaint}</p>}
      {order.diagnosis && <p className="text-sm"><span className="font-semibold">{t('workorder.diagnosis')} : </span>{order.diagnosis}</p>}
      {order.service_performed && <p className="text-sm"><span className="font-semibold">{t('workorder.performed')} : </span>{order.service_performed}</p>}
      {order.rts_notes && <p className="text-sm" data-testid="rts-notes"><span className="font-semibold">{t('workorder.rtsNotes')} : </span>{order.rts_notes}</p>}
      <Downtime order={order} />
      <WorkOrderParts orderId={order.id} assetId={assetId} open={live || order.state === 'repaired'} />
      <History entityType='fleet.work_order' entityId={order.id} />
      {w.ok && <Banner tone="ok">{w.ok}</Banner>}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
      {labour.permitted && live && !order._pending && (
        <Link to="/work/start" search={{ order: order.id }} className="btn w-full" data-testid="order-labour"><Picto name="clock" className="size-5" />{t('workorder.labourStart')}</Link>
      )}
      {canManage && (live || order.state === 'repaired') && mode === 'view' && !order._pending && (
        <div className="flex flex-wrap gap-2">
          {live && <button className="btn !min-h-11" onClick={() => { w.reset(); setMode('update') }} data-testid="order-update">{t('workorder.update')}</button>}
          <button className="btn !min-h-11" onClick={() => { w.reset(); setMode('repaired') }} data-testid="order-repaired">{order.state === 'repaired' ? t('workorder.editRts') : t('workorder.markRepaired')}</button>
          {live && <CancelButton orderId={order.id} />}
        </div>
      )}
      {mode === 'update' && (
        <form onSubmit={save} className="flex flex-col gap-3" data-testid="order-form">
          <Field label={t('workorder.stateLabel')} htmlFor={`st-${order.id}`}>
            <select id={`st-${order.id}`} className="input" value={state} onChange={(e) => setState(e.target.value as typeof state)}>
              {OPEN_WORK_ORDER_STATES.map((s) => <option key={s} value={s}>{t(`workorder.state.${s}`)}</option>)}
            </select>
          </Field>
          <Field label={t('workorder.diagnosis')} htmlFor={`dg-${order.id}`}><textarea id={`dg-${order.id}`} className="input" rows={3} maxLength={1000} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} /></Field>
          <Field label={t('workorder.performed')} htmlFor={`pf-${order.id}`}><textarea id={`pf-${order.id}`} className="input" rows={3} maxLength={1000} value={performed} onChange={(e) => setPerformed(e.target.value)} /></Field>
          <div className="flex gap-2"><button className="btn btn-primary flex-1" disabled={w.busy} data-testid="order-save">{t('common.save')}</button><button type="button" className="btn" onClick={() => setMode('view')}>{t('common.cancel')}</button></div>
        </form>
      )}
      {mode === 'repaired' && (
        <form onSubmit={repaired} className="flex flex-col gap-3" data-testid="repaired-form">
          <Field label={t('workorder.rtsNotes')} hint={t('workorder.rtsHelp')} htmlFor={`rts-${order.id}`}>
            <textarea id={`rts-${order.id}`} className="input" rows={3} maxLength={1000} required value={rts} onChange={(e) => setRts(e.target.value)} data-testid="rts-input" />
          </Field>
          <div className="flex gap-2"><button className="btn btn-primary flex-1" disabled={w.busy || !rts.trim()} data-testid="repaired-save">{t('workorder.markRepaired')}</button><button type="button" className="btn" onClick={() => setMode('view')}>{t('common.cancel')}</button></div>
        </form>
      )}
    </li>
  )
}

function CancelButton({ orderId }: { orderId: string }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  return (
    <span className="flex flex-col gap-2">
      <TwoTap label={t('workorder.cancel')} confirm={t('workorder.cancelConfirm')} danger disabled={w.busy} testId="order-cancel" className="!min-h-11"
        onConfirm={() => void w.run('workorder.cancel@1', { work_order_id: orderId }, 'workorder.cancelled')} />
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
    </span>
  )
}

function OpenOrderForm({ assetId }: { assetId: string }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [show, setShow] = useState(false)
  const [kind, setKind] = useState<'repair' | 'service' | 'inspection'>('repair')
  const [complaint, setComplaint] = useState('')
  useCloseOnPending(w.pending, () => setShow(false))         // kept on the device: the form closes, the line below says so (the command id stays: a retry is the same order)
  if (!show) {
    return (
      <div className="flex flex-col gap-2">
        {w.ok && <Banner tone="ok">{w.ok}</Banner>}
        {w.pending && <PendingBanner />}
        <button className="btn" onClick={() => { w.reset(); setShow(true) }} data-testid="order-new">{t('workorder.new')}</button>
      </div>
    )
  }
  return (
    <form className="card flex flex-col gap-3" data-testid="order-new-form" onSubmit={(e) => {
      e.preventDefault()
      void w.run('workorder.open@1', { work_order_id: id.current, asset_id: assetId, kind, ...(complaint.trim() ? { complaint: complaint.trim() } : {}) }, 'workorder.opened')
        .then((ok) => { if (ok) { id.current = newId(); setComplaint(''); setShow(false) } })
    }}>
      <Field label={t('workorder.kindLabel')} htmlFor="wo-kind">
        <select id="wo-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          {(['repair', 'service', 'inspection'] as const).map((k) => <option key={k} value={k}>{t(`workorder.kind.${k}`)}</option>)}
        </select>
      </Field>
      <Field label={t('workorder.complaint')} htmlFor="wo-complaint"><input id="wo-complaint" className="input" maxLength={500} value={complaint} onChange={(e) => setComplaint(e.target.value)} data-testid="order-complaint" /></Field>
      {w.error && <Banner tone="error">{w.error}</Banner>}
      <div className="flex gap-2"><button className="btn btn-primary flex-1" disabled={w.busy} data-testid="order-create">{t('workorder.create')}</button><button type="button" className="btn" onClick={() => setShow(false)}>{t('common.cancel')}</button></div>
    </form>
  )
}

/** The head greenkeeper or mechanic takes a machine out of service on purpose (a manual or service restriction). It ends only by a release. */
function RestrictForm({ assetId }: { assetId: string }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const { isBlocked } = useAvailability()
  const [show, setShow] = useState(false)
  const [kind, setKind] = useState<'manual' | 'service'>('manual')
  const [note, setNote] = useState('')
  useCloseOnPending(w.pending, () => setShow(false))
  if (!show) {
    return (
      <div className="flex flex-col gap-2">
        {w.ok && <Banner tone="ok">{w.ok}</Banner>}
        {w.pending && <PendingBanner />}
        <button className="btn" onClick={() => { w.reset(); setShow(true) }} data-testid="restrict-open">{isBlocked(assetId) ? t('safety.restrictAnother') : t('safety.restrict')}</button>
      </div>
    )
  }
  return (
    <form className="card flex flex-col gap-3" data-testid="restrict-form" onSubmit={(e) => {
      e.preventDefault()
      void w.run('asset.restrict@1', { restriction_id: id.current, asset_id: assetId, reason_kind: kind, note: note.trim() }, 'safety.restricted')
        .then((ok) => { if (ok) { id.current = newId(); setNote(''); setShow(false) } })
    }}>
      <p className="meta">{t('safety.restrictHelp')}</p>
      <Field label={t('safety.restrictKind')} htmlFor="rs-kind">
        <select id="rs-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="manual">{t('safety.kind.manual')}</option><option value="service">{t('safety.kind.service')}</option>
        </select>
      </Field>
      <Field label={t('safety.restrictNote')} htmlFor="rs-note"><input id="rs-note" className="input" required maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="restrict-note" /></Field>
      {w.error && <Banner tone="error">{w.error}</Banner>}
      <div className="flex gap-2"><button className="btn btn-danger flex-1" disabled={w.busy || !note.trim()} data-testid="restrict-go">{t('safety.restrictGo')}</button><button type="button" className="btn" onClick={() => setShow(false)}>{t('common.cancel')}</button></div>
    </form>
  )
}
